<template>
	<div class="page">
		<div class="content-sections">
			<div class="sgnl-flex sgnl-align-center repositories-toolbar sgnl-gap-8">
				<v-text-field
					v-model="githubSearchQuery"
					placeholder="Search public GitHub repositories..."
					hide-details
					class="github-search-input"
					data-test="github-search-input"
					@keydown.enter="openGithubSearchDialog"
				>
					<template #prepend-inner>
						<Search16 class="github-search-input__icon" />
					</template>
				</v-text-field>
				<v-btn
					variant="outlined"
					:disabled="!githubSearchQuery"
					@click="openGithubSearchDialog"
					data-test="open-github-search-button"
				>
					Search
				</v-btn>
				<v-spacer />
				<template v-if="!isUserLoggedInToGithub">
					<v-btn
						variant="flat"
						color="primary"
						class="github-login-btn"
						@click="githubLogin"
						data-test="github-login-button"
					>
						<LogoGithub16 />
						Login with GitHub
					</v-btn>
				</template>
				<template v-else>
					<v-hover v-slot="{ isHovering, props: hoverProps }">
						<v-btn
							v-bind="hoverProps"
							class="github-user-btn sgnl-paragraph-s-medium"
							:color="isHovering ? 'danger' : 'primary'"
							label
							variant="outlined"
							@click="githubLogout"
							:class="{ 'on-hover': isHovering, 'github-btn-hover': isHovering }"
							min-width="120px"
							:loading="!githubStore.githubUserDetails"
							data-test="github-user-button"
						>
							<div
								class="github-username"
								:style="{ visibility: isHovering ? 'hidden' : 'visible' }"
							>
								{{ formattedGithubUsername }}
							</div>
							<v-avatar
								max-height="60%"
								size="25"
								:style="{ visibility: isHovering ? 'hidden' : 'visible' }"
							>
								<v-img
									v-if="githubStore.githubUserDetails"
									:src="githubStore.githubUserDetails.avatar_url"
									:alt="githubStore.githubUsername"
									cover
								/>
							</v-avatar>
							<div
								v-if="isHovering"
								class="github-logout-overlay sgnl-text-color-error"
							>
								<span>GitHub Logout</span>
							</div>
						</v-btn>
					</v-hover>
					<v-btn
						variant="flat"
						color="accent"
						class="connect-repo-btn"
						@click="connectToRepo"
						data-test="connect-to-repositories-button"
					>
						<BookmarkAdd16 />
						Connect Repository
					</v-btn>
				</template>
			</div>
			<section class="sgnl-flex sgnl-flex-column sgnl-gap-16">
				<div class="section-title section-title--baseline">
					<h2 class="sgnl-h6-semibold sgnl-m-0">Connected Repositories</h2>
					<QuotaCount
						v-if="connectedRepositories"
						:used="connectedRepositories.length"
						limit-id="RepositoryCount"
					/>
				</div>
				<ErrorEmptyState
					v-if="repositoriesStore.loadError && connectedRepositories === undefined"
					title="Could not load Repositories"
					message="Often this is a temporary network or connectivity issue."
					:retrying="repositoriesStore.remoteRepositoryUpdateStatus.loading"
					bordered
					@retry="retryFetchRepositories"
				/>
				<div
					v-else-if="connectedRepositories === undefined"
					class="card-grid"
				>
					<v-card
						v-for="n in 3"
						:key="`repo-skeleton-${n}`"
						class="resource-card-shell sgnl-mx-0"
						variant="outlined"
						min-width="300px"
					>
						<div class="resource-card">
							<ConnectedRepositoryCardSkeleton withHeader />
						</div>
					</v-card>
				</div>
				<div
					v-else-if="connectedRepositories.length > 0"
					class="card-grid"
				>
					<ConnectedRepositoryCard
						v-for="cr in connectedRepositories"
						:key="cr.RepositoryID"
						:repositoryID="cr.RepositoryID"
						:repository="cr"
						@open-execution-log="openExecutionLog"
						@repository-disconnected="repoDisconnectionEventHandler"
						@repository-config-updated="repoConfigUpdateHandler"
						@repository-config-update-failed="repoConfigUpdateFailedHandler"
					/>
				</div>
				<EmptyState
					v-else
					header="No connected repositories"
					body="Search for repositories or connect your GitHub account"
					bordered
				>
					<template #actions>
						<v-btn
							v-if="!isUserLoggedInToGithub"
							variant="flat"
							color="primary"
							@click="githubLogin"
						>
							<LogoGithub16 />
							Login with GitHub
						</v-btn>
					</template>
				</EmptyState>
			</section>
		</div>
		<GithubAuthRedirectDialog
			:dialogVisible="githubAuthRedirectDialogActive"
			:dialogText="githubAuthRedirectDialogText"
		/>
		<GithubRepoListDialog
			v-if="githubRepoListDialogActive"
			:dialogVisible="githubRepoListDialogActive"
			@close-dialog="closeGithubRepoListDialog"
			@repo-connection-request="repoConnectionRequestHandler"
			@repository-disconnected="repoDisconnectionEventHandler"
		/>
		<SearchGithubReposDialog
			v-if="searchGithubReposDialogActive"
			:dialogVisible="searchGithubReposDialogActive"
			:searchQuery="githubSearchQuery"
			@close-dialog="closeSearchGithubReposDialog"
			@repo-connection-request="repoConnectionRequestHandler"
			@repository-disconnected="repoDisconnectionEventHandler"
			@github-login="githubLogin"
		/>
		<ExecutionResultsCard
			v-if="executionLogSheetActive"
			v-model:isVisible="executionLogSheetActive"
			:taskID="executionLogTaskID ?? undefined"
			:buildID="executionLogBuildID ?? undefined"
			@click-outside="closeExecutionLog"
		/>
		<RepositoryConnectionValidationCard
			v-if="repoConnectionValidationDialogActive && repoConnectionConfig"
			:dialogVisible="repoConnectionValidationDialogActive"
			:connectionConfig="repoConnectionConfig"
			@repository-connected="repoConnectionSuccessHandler"
			@close-dialog="repoConnectionValidationDialogActive = false"
		/>
		<v-snackbar
			v-model="successSnackbar"
			:timeout="4000"
			class="snackbar-success"
		>
			<div class="snackbar-icon-container snackbar-success">
				<CheckmarkFilled16 />
			</div>
			{{ successMessage }}
			<template v-slot:actions>
				<v-btn
					variant="text"
					size="small"
					icon
					@click="successSnackbar = false"
				>
					<Close16 class="sgnl-icon-color-secondary" />
				</v-btn>
			</template>
		</v-snackbar>
		<v-snackbar
			v-model="infoSnackbar"
			:timeout="4000"
			class="snackbar-info"
		>
			<div class="snackbar-icon-container snackbar-info">
				<InformationFilled16 />
			</div>
			{{ infoMessage }}
			<template v-slot:actions>
				<v-btn
					variant="text"
					size="small"
					icon
					@click="infoSnackbar = false"
				>
					<Close16 class="sgnl-icon-color-secondary" />
				</v-btn>
			</template>
		</v-snackbar>
		<v-snackbar
			v-model="errorSnackbar"
			:timeout="6000"
			class="snackbar-error"
		>
			<div class="snackbar-icon-container snackbar-error">
				<ErrorFilled16 />
			</div>
			{{ errorMessage }}
			<template v-slot:actions>
				<v-btn
					variant="text"
					size="small"
					icon
					@click="errorSnackbar = false"
				>
					<Close16 class="sgnl-icon-color-secondary" />
				</v-btn>
			</template>
		</v-snackbar>
	</div>
</template>

<script lang="ts">
/* eslint-disable vue/multi-word-component-names */
import { defineComponent } from "vue";

// Types
import { TierLimitEventTypeE } from "@/eventBus/tierLimitEventBus";
import { UserLimitsE } from "@/js/tierconfig";

// Libraries

// Utilities
import { initiateGithubLoginFlow, mockTimeout } from "@/js/githubClient";
import { isRepositoryConnected } from "@/js/repositoryUtilities";

// Components
import ConnectedRepositoryCard from "@/components/Repositories/ConnectedRepositoryCard.vue";
import ConnectedRepositoryCardSkeleton from "@/components/Repositories/ConnectedRepositoryCardSkeleton.vue";
import ErrorEmptyState from "@/components/Common/ErrorEmptyState.vue";
import EmptyState from "@/components/Common/EmptyState.vue";
import QuotaCount from "@/components/Common/QuotaCount.vue";
import GithubRepoListDialog from "@/components/Repositories/GithubRepoListDialog.vue";
import ExecutionResultsCard from "@/components/Repositories/ExecutionResultsCard.vue";
import SearchGithubReposDialog from "@/components/Repositories/SearchGithubReposDialog.vue";
import GithubAuthRedirectDialog from "@/components/Repositories/GithubAuthRedirectDialog.vue";
import RepositoryConnectionValidationCard from "@/components/Repositories/RepositoryConnectionValidationCard.vue";
import BookmarkAdd16 from "@carbon/icons-vue/es/bookmark--add/16";
import Search16 from "@carbon/icons-vue/es/search/16";
import LogoGithub16 from "@carbon/icons-vue/es/logo--github/16";
import CheckmarkFilled16 from "@carbon/icons-vue/es/checkmark--filled/16";
import InformationFilled16 from "@carbon/icons-vue/es/information--filled/16";
import ErrorFilled16 from "@carbon/icons-vue/es/error--filled/16";
import Close16 from "@carbon/icons-vue/es/close/16";

// Stores
import { mapState } from "pinia";
import { useCoresStore } from "@/stores/cores";
import { useUserStore } from "@/stores/user";
import { useRepositoriesStore } from "@/stores/repositories";
import { useRootStore } from "@/stores/root";

import { useGithubStore } from "@/stores/github";
import { useDataSourcesStore } from "@/stores/dataSources";
import { RepoConnectionConfig } from "@/types/general";

// Global variables

// Local Types

type ComponentData = {
	fetchingRepositories: boolean;
	githubRepoListDialogActive: boolean;
	searchGithubReposDialogActive: boolean;
	githubAuthRedirectDialogActive: boolean;
	githubAuthRedirectDialogText: string;
	repoConnectionValidationDialogActive: boolean;
	executionLogSheetActive: boolean;
	executionLogTaskID: undefined | string;
	executionLogBuildID: undefined | string;
	repoConnectionConfig: undefined | RepoConnectionConfig;
	successSnackbar: boolean;
	successMessage: string;
	infoSnackbar: boolean;
	infoMessage: string;
	errorSnackbar: boolean;
	errorMessage: string;
	githubSearchQuery: string;
};

export default defineComponent({
	name: "Repositories",
	components: {
		ConnectedRepositoryCard,
		ConnectedRepositoryCardSkeleton,
		ErrorEmptyState,
		EmptyState,
		QuotaCount,
		GithubRepoListDialog,
		ExecutionResultsCard,
		SearchGithubReposDialog,
		GithubAuthRedirectDialog,
		RepositoryConnectionValidationCard,
		BookmarkAdd16,
		Search16,
		LogoGithub16,
		CheckmarkFilled16,
		InformationFilled16,
		ErrorFilled16,
		Close16,
	},
	data: (): ComponentData => ({
		fetchingRepositories: false,
		githubRepoListDialogActive: false,
		searchGithubReposDialogActive: false,
		githubAuthRedirectDialogActive: false,
		githubAuthRedirectDialogText: "",
		repoConnectionValidationDialogActive: false,
		executionLogSheetActive: false,
		executionLogTaskID: undefined,
		executionLogBuildID: undefined,
		repoConnectionConfig: undefined,
		successSnackbar: false,
		successMessage: "",
		infoSnackbar: false,
		infoMessage: "",
		errorSnackbar: false,
		errorMessage: "",
		githubSearchQuery: "",
	}),
	setup() {
		const coresStore = useCoresStore();
		const repositoriesStore = useRepositoriesStore();
		const githubStore = useGithubStore();
		const userStore = useUserStore();
		const rootStore = useRootStore();

		return { coresStore, repositoriesStore, githubStore, userStore, rootStore };
	},
	computed: {
		...mapState(useDataSourcesStore, { defaultRepoMountConfig: "defaultMountConfig" }),
		formattedGithubUsername(): string {
			return this.githubStore.githubUsername || "-";
		},
		connectedRepositories() {
			return useRepositoriesStore().connectedRepositories;
		},
		isUserLoggedInToGithub(): boolean {
			const githubStore = useGithubStore();
			return githubStore.userGithubLogin;
		},
	},
	methods: {
		connectToRepo() {
			//@ts-ignore
			this.$posthog?.capture("search_private_repos_button_clicked");

			if (!this.githubStore.githubLoggedIn) {
				this.githubLogin();
			} else {
				this.githubRepoListDialogActive = true;
			}
		},
		closeGithubRepoListDialog() {
			this.githubRepoListDialogActive = false;
		},
		closeSearchGithubReposDialog() {
			this.searchGithubReposDialogActive = false;
		},
		githubLogin() {
			//@ts-ignore
			this.$posthog?.capture("github_login_initiated");

			this.githubAuthRedirectDialogText = "Authenticating with GitHub...";
			this.githubAuthRedirectDialogActive = true;
			mockTimeout(500).then(() => {
				initiateGithubLoginFlow();
			});
		},
		async githubLogout() {
			// TODO: Test
			this.githubAuthRedirectDialogText = "Disconnecting from GitHub...";
			this.githubAuthRedirectDialogActive = true;

			await this.githubStore.githubLogout();
			mockTimeout(500).then(() => {
				window.location.reload();
			});
		},
		repoConnectionSuccessHandler(repoData: RepoConnectionConfig, alreadyConnected: boolean) {
			this.repositoriesStore.fetchRemoteRepositories();
			this.repoConnectionConfig = undefined;
			if (alreadyConnected) {
				this.infoMessage = "Repository already connected.";
				this.infoSnackbar = true;
			} else {
				// @ts-ignore
				this.$posthog?.capture("repository_connected", {
					repoURL: repoData.remoteURL,
				});
				this.successMessage = "Repository connected successfully.";
				this.successSnackbar = true;
			}
		},
		repoConnectionRequestHandler(config: RepoConnectionConfig) {
			this.repoConnectionConfig = config;
			this.repoConnectionValidationDialogActive = true;
		},
		repoDisconnectionEventHandler() {
			// @ts-ignore
			this.$posthog?.capture("repository_disconnected");

			this.repositoriesStore.fetchRemoteRepositories();
			this.rootStore.tierLimitEventBus.emit({
				type: TierLimitEventTypeE.UsageChanged,
				affectedLimits: [UserLimitsE.RepositoryCount],
			});

			this.successMessage = "Repository disconnected successfully.";
			this.successSnackbar = true;
		},
		repoConfigUpdateHandler(message: string) {
			this.successMessage = message;
			this.successSnackbar = true;
		},
		repoConfigUpdateFailedHandler(message: string) {
			this.errorMessage = message || "Repository update failed. Please try again.";
			this.errorSnackbar = true;
		},
		buildAndRun() {},
		openExecutionLog(data: { taskID: string; buildID: string }) {
			if (!this.executionLogSheetActive) {
				this.executionLogTaskID = data.taskID;
				this.executionLogBuildID = data.buildID;
				this.executionLogSheetActive = true;
			}
		},
		closeExecutionLog() {
			this.executionLogTaskID = undefined;
			this.executionLogSheetActive = false;
		},
		openGithubSearchDialog() {
			if (!this.githubSearchQuery) return;

			//@ts-ignore
			this.$posthog?.capture("search_public_repos_button_clicked");

			this.searchGithubReposDialogActive = false;
			this.$nextTick(() => {
				this.searchGithubReposDialogActive = true;
			});
		},
		async retryFetchRepositories() {
			await this.repositoriesStore.fetchRemoteRepositories();
		},
	},
	async mounted() {
		// Deal with repo connection with `?connect` query param
		if (this.$route.query?.connect && typeof this.$route.query?.connect === "string") {
			const urlOfRepoToConnect = new URL(this.$route.query.connect);
			if (urlOfRepoToConnect.host === "github.com") {
				const config: RepoConnectionConfig = { remoteURL: urlOfRepoToConnect };
				if (await isRepositoryConnected(urlOfRepoToConnect)) {
					this.repoConnectionSuccessHandler(config, true);
				} else {
					this.repoConnectionRequestHandler(config);
				}
			} else {
				this.errorMessage = `Non-GitHub repositories are not supported yet, so this ${urlOfRepoToConnect.host} repository was not connected.`;
				this.errorSnackbar = true;
			}
			this.$router.push({ name: "Repositories" });
		}

		// wait until till github details are fetched before fetching repositories
		await this.repositoriesStore.fetchRemoteRepositories();
	},
});
</script>

<style lang="scss" scoped>
.card-grid {
	display: grid;
	gap: var(--sgnl-space-16);
	grid-template-columns: 1fr;
}

.github-search-input {
	min-width: 240px;
	max-width: 300px;
}

@media (min-width: 960px) {
	.github-search-input :deep(.v-field__input) {
		min-height: 36px;
	}
}

.github-user-btn {
	max-width: 240px;
	padding-inline: var(--sgnl-space-8);
	text-align: center;
}

.github-username {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	min-width: 0;
}

.github-search-input :deep(.v-field__input) {
	padding-top: 0;
	padding-bottom: 0;
	align-self: center;
}

.github-search-input__icon {
	color: var(--sgnl-default-secondary-icon);
}

@media (max-width: 599px) {
	.repositories-toolbar {
		flex-wrap: wrap;
	}

	.github-search-input {
		max-width: 100%;
		flex: 1 1 100%;
	}

	.repositories-toolbar > .v-spacer {
		display: none;
	}

	.github-login-btn,
	.connect-repo-btn,
	.github-user-btn {
		flex: 1 1 100%;
	}

	.github-user-btn {
		max-width: none;
	}
}

.github-btn-hover {
	box-shadow: 0 0 0 var(--sgnl-border-width-2) var(--sgnl-focus-ring-error);
}

.github-logout-overlay {
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: white;
	border-radius: var(--sgnl-border-radius-6);
}
</style>
